import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Upload, Star } from "lucide-react";
import { api, fileUrl, errMsg, CATEGORIES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CatSelect } from "./CatSelect";

function UploadForm({ onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("animales");
  const [featured, setFeatured] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Elija una foto o un video");
    const fd = new FormData();
    Object.entries({ file, title, category, featured }).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      await api.post("/admin/media", fd);
      toast.success("Archivo subido");
      setFile(null); setTitle(""); setFeatured(false); e.target.reset();
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card-fv p-6 grid gap-4 md:grid-cols-12 items-end" data-testid="admin-media-upload-form">
      <div className="md:col-span-3 space-y-2"><Label>Foto o video</Label>
        <Input type="file" accept="image/*,video/*" onChange={(e) => setFile(e.target.files[0])} data-testid="admin-media-file-input" /></div>
      <div className="md:col-span-3 space-y-2"><Label>Título</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-media-title-input" /></div>
      <div className="md:col-span-2 space-y-2"><Label>Categoría</Label><CatSelect value={category} onChange={setCategory} testid="admin-media-category-select" /></div>
      <label className="md:col-span-2 flex items-center gap-2 h-10 text-sm"><Switch checked={featured} onCheckedChange={setFeatured} data-testid="admin-media-featured-switch" />En portada</label>
      <button disabled={busy} className="md:col-span-2 btn-fv btn-red justify-center !py-2.5 disabled:opacity-60" data-testid="admin-media-upload-button"><Upload size={16} />{busy ? "Subiendo…" : "Subir"}</button>
    </form>
  );
}

export const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/media").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => { try { await api.patch(`/admin/media/${id}`, body); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (id) => { if (!window.confirm("¿Quitar este archivo de la galería?")) return; await api.delete(`/admin/media/${id}`); toast.success("Quitado"); load(); };
  const move = async (i, d) => {
    const ids = items.map((m) => m.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post("/admin/media/reorder", { ids });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      <UploadForm onDone={load} />
      <p className="text-sm text-[#5C635E]">{items.length} elementos · El primer video o foto marcado "En portada" se muestra como fondo del inicio.</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((m, i) => (
          <div key={m.id} className="card-fv overflow-hidden" data-testid={`admin-media-item-${m.id}`}>
            <div className="relative aspect-video bg-[#1D3324]">
              {m.type === "video" ? <video src={fileUrl(m.url)} muted controls preload="metadata" className="h-full w-full object-cover" /> : <img src={fileUrl(m.url)} alt="" className="h-full w-full object-cover" />}
              {m.featured && <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-[#C5A059] px-2 py-0.5 text-xs"><Star size={12} />Portada</span>}
            </div>
            <div className="p-4 space-y-3">
              <Input defaultValue={m.title} onBlur={(e) => e.target.value !== m.title && patch(m.id, { title: e.target.value })} data-testid={`admin-media-edit-title-${m.id}`} />
              <div className="flex items-center gap-3">
                <div className="flex-1"><CatSelect value={m.category} onChange={(v) => patch(m.id, { category: v })} testid={`admin-media-edit-category-${m.id}`} /></div>
                <label className="flex items-center gap-2 text-xs"><Switch checked={m.featured} onCheckedChange={(v) => patch(m.id, { featured: v })} data-testid={`admin-media-edit-featured-${m.id}`} />Portada</label>
              </div>
              <div className="flex justify-between">
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} className="p-2 hover:bg-stone-100" data-testid={`admin-media-up-${m.id}`} aria-label="Subir"><ArrowUp size={16} /></button>
                  <button onClick={() => move(i, 1)} className="p-2 hover:bg-stone-100" data-testid={`admin-media-down-${m.id}`} aria-label="Bajar"><ArrowDown size={16} /></button>
                </div>
                <button onClick={() => remove(m.id)} className="p-2 text-red-700 hover:bg-red-50" data-testid={`admin-media-delete-${m.id}`} aria-label="Quitar"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export { CATEGORIES };
